import { Box, Tab, Tabs, Typography, Button, Switch } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';

import AnimationPage from '../../pages/AnimationPage';
import ClassroomSettings from './classroom/ClassroomSettings';
import PrivateLessonSettings from './PrivateLessonSettings/PrivateLessonSettings';
import GroupLessonSettings from './GroupLessonSettings/GroupLessonSetting';
import AutoAttendanceSettings from './AutoAttendanceSettings';
import AppStopSettings from './AppStopSettings';
import { getSalaryPeriod, saveSalaryPeriod } from '../../api/salarySetting';

function StoreTab3({ roomListInfo, setRoomListInfo, branchId, getDetailCenter, branchDetailInfo, setSelectedTab }) {
  const { t } = useTranslation();

  // 급여정산 화면의 "설정 바로가기"처럼 특정 서브탭(예: 급여 설정=4)으로 바로 진입할 수 있도록
  // URL의 subtab 파라미터와 동기화한다. StoreDetails의 상위 tab 파라미터와는 별개다.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedTabChild, setSelectedTabChild] = useState(() => Number(searchParams.get('subtab')) || 1);
  const classroomSettingsRef = useRef(null);

  useEffect(() => {
    const next = Number(searchParams.get('subtab'));
    if (next && next !== selectedTabChild) setSelectedTabChild(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleChildTabChange = (_event, newValue) => {
    setSelectedTabChild(newValue);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('subtab', newValue);
      return next;
    });
  };

  return (
    <AnimationPage>
      <Box py={4}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Tabs
            sx={{
              '& .MuiTab-root': {
                borderRadius: '8px',
                border: '1px solid #ddd',
                mr: 1.25,
                minWidth: '100px',
                minHeight: '36px !important',
                padding: '8px 20px',
                fontWeight: 500,
                fontSize: '13px',
                color: '#666',
                textTransform: 'none',
              },
              '& .MuiTab-root.Mui-selected': {
                background: '#262626',
                border: '1px solid #262626',
                color: '#fff',
              },
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}
            value={selectedTabChild}
            onChange={handleChildTabChange}
            aria-label="lesson settings tabs">
            <Tab value={1} label={t('Classroom Settings')} />
            <Tab value={2} label={t('Private Lesson Settings')} />
            <Tab value={3} label={t('Group Lesson Settings')} />
            <Tab value={4} label={t('급여 설정')} />
            <Tab value={5} label={t('자동 출결 설정')} />
            <Tab value={6} label={t('회원앱 중지')} />
          </Tabs>
          {selectedTabChild === 1 && branchId && (
            <Button
              onClick={() => classroomSettingsRef.current?.submitForm()}
              size="small"
              startIcon={<AddIcon />}
              sx={{
                fontSize: '13px',
                height: '36px',
                color: '#262626',
                border: '1px solid #262626',
                borderRadius: '8px',
                px: 2,
                '&:hover': { backgroundColor: '#000000', color: '#fff' },
              }}>
              {t('Add classroom')}
            </Button>
          )}
        </Box>
        {selectedTabChild === 1 && (
          <ClassroomSettings
            ref={classroomSettingsRef}
            roomListInfo={roomListInfo}
            setRoomListInfo={setRoomListInfo}
            branchId={branchId}
            getDetailCenter={getDetailCenter}
            branchDetailInfo={branchDetailInfo}
          />
        )}
        {selectedTabChild === 2 && (
          <PrivateLessonSettings
            roomListInfo={roomListInfo}
            setRoomListInfo={setRoomListInfo}
            branchId={branchId}
            getDetailCenter={getDetailCenter}
            branchDetailInfo={branchDetailInfo}
          />
        )}
        {selectedTabChild === 3 && (
          <GroupLessonSettings
            roomListInfo={roomListInfo}
            setRoomListInfo={setRoomListInfo}
            branchId={branchId}
            getDetailCenter={getDetailCenter}
            branchDetailInfo={branchDetailInfo}
          />
        )}
        {selectedTabChild === 4 && (
          <SalarySettingsSection branchId={branchId} />
        )}
        {selectedTabChild === 5 && (
          <AutoAttendanceSettings branchId={branchId} />
        )}
        {selectedTabChild === 6 && <AppStopSettings branchId={branchId} />}
      </Box>
    </AnimationPage>
  );
}

// ============================================
// 급여 설정 섹션
// ============================================
function SalarySettingsSection({ branchId }) {
  const { t } = useTranslation();
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: periodData } = useQuery({
    queryKey: ['salaryPeriod', branchId],
    queryFn: () => getSalaryPeriod(branchId, user.token),
    enabled: !!branchId,
  });

  const [signedOnlyYn, setSignedOnlyYn] = useState('N');

  useEffect(() => {
    if (periodData) {
      setSignedOnlyYn(periodData.signedOnlyYn || 'N');
    }
  }, [periodData]);

  const saveMutation = useMutation({
    mutationFn: () => saveSalaryPeriod({
      branchId,
      startDay: periodData?.startDay || 1,
      endDay: periodData?.endDay || 31,
      signedOnlyYn,
    }, user.token),
    onSuccess: () => {
      toast.success('설정이 저장되었습니다');
      queryClient.invalidateQueries({ queryKey: ['salaryPeriod'] });
    },
  });

  return (
    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Box sx={{
        background: '#fff', borderRadius: '12px', border: '1px solid #E8E8E8',
        padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px',
      }}>
        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#262626' }}>
          {t('수업 정산 설정')}
        </Typography>

        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', background: '#F7F9FA', borderRadius: '8px',
        }}>
          <Box>
            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#333' }}>
              수업 사인 완료된 수업만 정산
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#888', mt: '4px' }}>
              활성화 시 강사가 강사앱에서 수업 사인을 완료한 수업만 급여 정산에 포함됩니다.
            </Typography>
          </Box>
          <Switch
            checked={signedOnlyYn === 'Y'}
            onChange={() => setSignedOnlyYn(prev => prev === 'Y' ? 'N' : 'Y')}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            sx={{
              background: '#262626', color: '#fff', borderRadius: '8px',
              padding: '8px 24px', fontSize: '14px', fontWeight: 600,
              '&:hover': { background: '#000000' },
              '&.Mui-disabled': { background: '#ccc' },
            }}
          >
            {saveMutation.isPending ? '저장 중...' : '저장'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default StoreTab3;
